import { Link } from "react-router-dom";
import { getImageUrl } from "../../utils/imageUtils";
import { Store, Mail, Phone } from "lucide-react";
import { useState, useEffect } from "react";
import axios from "axios";
import { Dialog, DialogContent, DialogTrigger } from "@/components/ui/dialog"; // Assuming we might need dialogs here too, but likely just links
// Actually the footer in LandingPage triggered the dialog for "Become a Reseller"

import { usePlatformContext } from "../../context/PlatformContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const publicAxios = axios.create({ withCredentials: false });

export default function Footer({ onOpenResellerDialog }) {
    const { platformInfo } = usePlatformContext();
    const [contentPages, setContentPages] = useState([]);

    useEffect(() => {
        fetchContentPages();
    }, []);

    const fetchContentPages = async () => {
        try {
            const response = await publicAxios.get(`${API}/pages/public`);
            setContentPages(response.data.filter(p => p.show_in_footer));
        } catch (error) {
            console.error("Failed to fetch footer pages:", error);
        }
    };

    return (
        <footer className="py-12 px-4 sm:px-6 lg:px-8 bg-slate-900 text-slate-400">
            <div className="max-w-7xl mx-auto">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
                    {/* Brand */}
                    <div className="flex flex-col gap-3">
                        <div className="flex items-center gap-2">
                            {platformInfo.platform_logo_url ? (
                                <img
                                    src={getImageUrl(platformInfo.platform_logo_url)}
                                    alt={platformInfo.platform_name}
                                    className="h-10 w-auto object-contain"
                                />
                            ) : (
                                <div className="w-10 h-10 bg-gradient-to-br from-amber-500 to-amber-600 rounded-lg flex items-center justify-center">
                                    <Store className="w-5 h-5 text-white" />
                                </div>
                            )}
                            <div className="flex flex-col">
                                <span className="text-white font-semibold text-lg">
                                    {platformInfo.platform_name || "eDisplayroom"}
                                </span>
                                <span className="text-xs text-slate-400 -mt-1">
                                    {platformInfo.platform_tagline || "by Infinity Funeral Supply"}
                                </span>
                            </div>
                        </div>
                        <p className="text-sm text-slate-500 mt-2">
                            Your trusted partner for reseller storefronts in the funeral supply industry.
                        </p>
                    </div>

                    {/* Quick Links */}
                    <div className="flex flex-col gap-3">
                        <h4 className="text-white font-semibold mb-2">Quick Links</h4>
                        <Link to="/login" className="text-sm hover:text-amber-500 transition-colors">
                            Login
                        </Link>
                        {onOpenResellerDialog ? (
                            <button
                                onClick={onOpenResellerDialog}
                                className="text-sm hover:text-amber-500 transition-colors text-left"
                            >
                                Become a Reseller
                            </button>
                        ) : (
                            <Link to="/reseller-program" className="text-sm hover:text-amber-500 transition-colors text-left">
                                Become a Reseller
                            </Link>
                        )}
                        <Link to="/products" className="text-sm hover:text-amber-500 transition-colors">
                            Shop All Products
                        </Link>
                        <Link to="/collections" className="text-sm hover:text-amber-500 transition-colors">
                            Collections
                        </Link>
                        {contentPages.map(page => (
                            <a
                                key={page._id}
                                href={`/${page.slug}.html`}
                                className="text-sm hover:text-amber-500 transition-colors"
                            >
                                {page.title}
                            </a>
                        ))}
                    </div>

                    {/* Contact Info */}
                    <div className="flex flex-col gap-3">
                        <h4 className="text-white font-semibold mb-2">Contact Us</h4>
                        {platformInfo.support_email && (
                            <a
                                href={`mailto:${platformInfo.support_email}`}
                                className="flex items-center gap-2 text-sm hover:text-amber-500 transition-colors"
                            >
                                <Mail className="w-4 h-4" />
                                {platformInfo.support_email}
                            </a>
                        )}
                        {platformInfo.support_phone && (
                            <a
                                href={`tel:${platformInfo.support_phone}`}
                                className="flex items-center gap-2 text-sm hover:text-amber-500 transition-colors"
                            >
                                <Phone className="w-4 h-4" />
                                {platformInfo.support_phone}
                            </a>
                        )}
                    </div>
                </div>

                {/* Bottom Bar */}
                <div className="border-t border-slate-800 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
                    <p className="text-sm text-center md:text-left font-medium">
                        {platformInfo.footer_copyright_text || `© ${new Date().getFullYear()} ${platformInfo.platform_name || "eDisplayroom"} ${platformInfo.platform_tagline || "by Infinity Funeral Supply"}. All rights reserved.`}
                    </p>
                    <div className="flex items-center gap-2 text-[10px] text-slate-600 uppercase tracking-widest font-bold">
                        <span>Managed by</span>
                        <span className="text-amber-600/50">Infinity Funeral Supply</span>
                    </div>
                </div>
            </div>
        </footer>
    );
}
